<template>
    <!-- Sem Teleport: o widget mora dentro de `.admin-shell` (AdminPage), que é
         onde valem os botões do design system do admin. `position: fixed` basta. -->
    <div ref="raiz" class="oav" :class="{ 'oav--aberto': aberto, 'oav--arrastando': arrastando }" :style="estiloArraste" v-bind="eventosArraste">
            <!-- Fechado: um selo que diz se há algo acontecendo -->
            <button v-if="!aberto" type="button" class="oav-pill" :class="tomPill" @click="abrir"
                v-tooltip="'Onboardings Meta ao vivo — conexões em andamento, falhas recentes e números aguardando Conversation Routing.'">
                <span class="oav-dot" :class="{ 'oav-dot--pulsa': contagem.andamento > 0 }"></span>
                <i class="ri-node-tree"></i>
                <span v-if="!store.lista.length">Onboarding: nada agora</span>
                <template v-else>
                    <span v-if="contagem.andamento">{{ contagem.andamento }} em andamento</span>
                    <span v-if="contagem.falhas" class="oav-pill__sep">{{ contagem.falhas }} com falha</span>
                    <span v-if="contagem.roteamento" class="oav-pill__sep">{{ contagem.roteamento }} aguardando roteamento</span>
                    <span v-if="contagem.operador" class="oav-pill__sep">{{ contagem.operador }} com operador</span>
                    <span v-if="!contagem.andamento && !contagem.falhas && !contagem.roteamento && !contagem.operador">{{ store.lista.length }} recentes</span>
                </template>
            </button>

            <!-- Aberto -->
            <section v-else class="oav-painel" aria-label="Onboardings Meta ao vivo">
                <header class="oav-head">
                    <div class="d-flex align-items-center gap-2">
                        <span class="oav-dot" :class="{ 'oav-dot--pulsa': contagem.andamento > 0 }"></span>
                        <strong>Onboarding ao vivo</strong>
                        <span class="oav-modo" :class="`oav-modo--${store.modo}`" v-tooltip="ajudaModo">{{ rotuloModo }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-1">
                        <ButtonField type="ghost" size="small" icon="ri-external-link-line" tooltip="Todas as execuções" @click="$router.push('/admin-meta-onboarding')" />
                        <ButtonField type="ghost" size="small" icon="ri-refresh-line" tooltip="Recarregar agora" @click="store.carregar()" />
                        <ButtonField type="ghost" size="small" icon="ri-subtract-line" tooltip="Minimizar" @click="fechar" />
                    </div>
                </header>

                <div v-if="!store.lista.length" class="oav-vazio">
                    <i class="ri-checkbox-circle-line"></i>
                    Nenhuma conexão Meta em andamento, nenhuma falha nos últimos 30 min e nenhum número aguardando roteamento.
                </div>

                <ul v-else class="oav-lista">
                    <li v-for="i in store.lista" :key="i.idExecucao" class="oav-item" :class="`oav-item--${tomItem(i)}`">
                        <div class="oav-item__topo">
                            <StatusBadge :label="statusRotulo(i.status)" :tone="statusTom(i.status)" />
                            <span class="oav-fluxo">{{ fluxoRotulo(i.fluxo) }}</span>
                            <span class="oav-tempo" v-tooltip="`Início ${dataHora(i.iniciadoEm)} · última atividade ${dataHora(i.ultimaAtividadeEm)}`">
                                {{ duracao(i) }}
                            </span>
                        </div>
                        <div class="oav-quem">
                            <strong>{{ i.nomeInbox || i.numero || 'inbox ainda não criada' }}</strong>
                            <span v-if="i.nomeConta"> · {{ i.nomeConta }}</span>
                            <span class="text-muted"> · {{ origemRotulo(i.origem) }}<template v-if="i.nomeUsuario"> ({{ i.nomeUsuario }})</template></span>
                        </div>

                        <!-- Onde está agora / onde parou -->
                        <div class="oav-agora">
                            <template v-if="i.status === 'EM_ANDAMENTO'">
                                <span class="spinner-border spinner-border-sm"></span>
                                <span>Agora: <strong>{{ etapaAtual(i) }}</strong></span>
                            </template>
                            <template v-else-if="i.status === 'FALHOU' || i.status === 'ABANDONADA'">
                                <i class="ri-close-circle-line text-danger"></i>
                                <span>Parou em <strong>{{ i.etapaDecisiva ? rotuloEtapa(i.etapaDecisiva) : etapaAtual(i) }}</strong></span>
                            </template>
                            <template v-else>
                                <i class="ri-check-double-line text-success"></i>
                                <span>Concluída em <strong>{{ i.etapaDecisiva ? rotuloEtapa(i.etapaDecisiva) : '—' }}</strong></span>
                            </template>
                        </div>
                        <div v-if="i.desfechoResumo && i.status !== 'CONCLUIDA'" class="oav-erro">{{ i.desfechoResumo }}</div>

                        <!-- Operador (WAME fase 3) -->
                        <div v-if="i.operador" class="oav-operador">
                            <i class="ri-customer-service-2-line"></i>
                            <span><strong>{{ i.operador.situacao === 'EXECUTANDO' ? 'Operador executando' : 'Esperando operador' }}</strong>
                                · segue com o padrão às {{ dataHora(i.operador.expiraEm) }}<template v-if="i.operador.operadorNome"> · {{ i.operador.operadorNome }}</template></span>
                            <ButtonField class="ms-auto" type="primary" size="small" icon="ri-customer-service-2-line" text="Abrir painel"
                                @click="$router.push({ path: '/admin-whatsapp-wame', query: { sessao: i.operador.idSessao } })" />
                        </div>

                        <!-- WAME -->
                        <div v-if="i.wame" class="oav-wame">
                            <StatusBadge :label="i.wame.situacao === 'AGUARDANDO_ROTEAMENTO' ? 'Aguardando Conversation Routing' : `WAME: ${i.wame.situacao}`"
                                :tone="i.wame.situacao === 'AGUARDANDO_ROTEAMENTO' ? 'amber' : i.wame.situacao === 'INATIVO' ? 'red' : 'teal'" icon="ri-route-line" />
                            <span class="text-muted">número {{ i.wame.origemNumero }}</span>
                            <span v-if="i.wame.fundingPresente === false" class="text-danger">· sem forma de pagamento</span>
                        </div>

                        <!-- Linha do tempo compacta (mais antiga → mais nova) -->
                        <ol class="oav-etapas">
                            <li v-for="(e, k) in etapasCronologicas(i)" :key="k" :class="`oav-etapa--${e.status}`"
                                v-tooltip="`${rotuloEtapa(e.etapa)} — ${e.status}${e.httpStatus ? ` · HTTP ${e.httpStatus}` : ''}${e.erroCode ? ` · code ${e.erroCode}` : ''}${e.duracaoMs ? ` · ${e.duracaoMs} ms` : ''}${e.erroMensagem ? `\n${e.erroMensagem}` : ''}`">
                                <i :class="e.status === 'SUCESSO' ? 'ri-check-line' : e.status === 'FALHA' ? 'ri-close-line' : 'ri-loader-4-line'"></i>
                                <span>{{ rotuloEtapa(e.etapa) }}</span>
                            </li>
                        </ol>

                        <div class="oav-acoes">
                            <router-link :to="{ path: '/admin-meta-onboarding', query: { execucao: i.idExecucao } }">Linha do tempo</router-link>
                            <router-link v-if="i.idInbox && i.fluxo.startsWith('WHATSAPP')" :to="{ path: '/admin-whatsapp-wame', query: { inbox: i.idInbox } }">WAME</router-link>
                            <router-link v-if="i.idInbox" :to="{ path: '/admin-meta-graph-console', query: { idInbox: i.idInbox } }">Graph</router-link>
                            <ButtonField v-if="i.status === 'EM_ANDAMENTO' || i.operador || i.wame?.situacao === 'AGUARDANDO_ROTEAMENTO'" class="ms-auto oav-encerrar"
                                type="ghost" size="small" icon="ri-stop-circle-line" text="Encerrar" tooltip="Dar esta execução por encerrada" @click="encerrar(i)" />
                        </div>
                        <div v-if="i.anomalias.length" class="oav-anomalias"><i class="ri-alarm-warning-line"></i> {{ i.anomalias.join(' · ') }}</div>
                    </li>
                </ul>
                <footer class="oav-rodape">Atualizado {{ store.ultimaAtualizacao ? dataHora(store.ultimaAtualizacao) : '—' }}</footer>
            </section>
    </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, ref } from 'vue';
import { useArrastavel } from '@/composables/useArrastavel';
import { encerrarExecucaoOnboarding } from './encerrarExecucao';
import StatusBadge from '@/components/global/StatusBadge.vue';
import { useOnboardingAoVivoStore } from '@/stores/admin/onboardingAoVivo';
import { ROTULO_FLUXO } from '@/services/meta-trilha/meta-trilha.service';
import { ROTULO_ORIGEM_ONBOARDING, ROTULO_STATUS_EXECUCAO, rotuloEtapa } from '@/services/meta-trilha/meta-onboarding-execucoes.service';
import type { ItemAoVivo } from '@/services/meta-trilha/meta-onboarding-ao-vivo.service';

const CHAVE_ABERTO = 'admin:onboarding-ao-vivo:aberto';

/**
 * Widget SEMPRE visível em toda página do admin (montado pelo `AdminPage`):
 * onboardings Meta acontecendo agora, onde cada um está, onde parou e o estado
 * do Conversation Routing (WAME). Tempo real por SSE, com fallback em polling.
 */
export default defineComponent({
    name: 'OnboardingAoVivoWidget',
    components: { StatusBadge },
    setup() {
        // Arrastável pela pílula e pelo cabeçalho; duplo clique volta ao canto.
        const raiz = ref<HTMLElement | null>(null);
        const { estilo, eventos, arrastando, reencaixar, acabouDeArrastar } = useArrastavel(raiz, {
            chave: 'admin:onboarding-ao-vivo:pos',
            seletorPunho: '.oav-pill, .oav-head',
        });
        return { raiz, estiloArraste: estilo, eventosArraste: eventos, arrastando, reencaixar, acabouDeArrastar };
    },
    data: () => ({
        store: useOnboardingAoVivoStore(),
        aberto: false,
        agora: Date.now(),
        relogio: 0 as unknown as ReturnType<typeof setInterval>,
    }),
    computed: {
        contagem() { return this.store.contagem; },
        tomPill(): string {
            if (this.contagem.operador) return 'oav-pill--operador';
            if (this.contagem.falhas) return 'oav-pill--erro';
            if (this.contagem.andamento) return 'oav-pill--vivo';
            if (this.contagem.roteamento) return 'oav-pill--alerta';
            return '';
        },
        rotuloModo(): string {
            return { sse: 'ao vivo', polling: 'a cada 5 s', conectando: 'conectando…', desligado: 'desligado' }[this.store.modo];
        },
        ajudaModo(): string {
            return this.store.modo === 'sse'
                ? 'Conectado por SSE: cada etapa aparece aqui no instante em que é gravada, em qualquer réplica.'
                : 'SSE indisponível (sessão, proxy ou NATS): atualizando a cada 5 s e tentando voltar ao SSE a cada 30 s.';
        },
    },
    mounted() {
        this.store.iniciar();
        try { this.aberto = localStorage.getItem(CHAVE_ABERTO) === '1'; } catch { this.aberto = false; }
        this.relogio = setInterval(() => { this.agora = Date.now(); }, 1000);
    },
    beforeUnmount() {
        this.store.parar();
        clearInterval(this.relogio);
    },
    methods: {
        rotuloEtapa,
        statusRotulo(s: string): string { return (ROTULO_STATUS_EXECUCAO as Record<string, { rotulo: string }>)[s]?.rotulo ?? s; },
        statusTom(s: string): string { return (ROTULO_STATUS_EXECUCAO as Record<string, { tom: string }>)[s]?.tom ?? 'slate'; },
        fluxoRotulo(f: string): string { return (ROTULO_FLUXO as Record<string, string>)[f] ?? f; },
        origemRotulo(o: string): string { return (ROTULO_ORIGEM_ONBOARDING as Record<string, string>)[o] ?? o; },
        async encerrar(i: ItemAoVivo) {
            const ok = await encerrarExecucaoOnboarding({
                idExecucao: i.idExecucao,
                rotulo: i.nomeInbox || i.numero || i.nomeConta || i.idExecucao,
                temOperador: !!i.operador,
                roteamentoPendente: i.wame?.situacao === 'AGUARDANDO_ROTEAMENTO',
            });
            if (ok) await this.store.carregar();
        },
        abrir() {
            // O clique que encerra o arraste da pílula não abre o painel.
            if (this.acabouDeArrastar()) return;
            this.aberto = true;
            this.lembrar();
            void nextTick(() => this.reencaixar());
        },
        fechar() { this.aberto = false; this.lembrar(); },
        lembrar() { try { localStorage.setItem(CHAVE_ABERTO, this.aberto ? '1' : '0'); } catch { /* sem storage */ } },
        dataHora(iso: string) { return new Date(iso).toLocaleTimeString('pt-BR'); },
        duracao(i: ItemAoVivo): string {
            const fim = i.encerradoEm ? new Date(i.encerradoEm).getTime() : this.agora;
            const s = Math.max(0, Math.round((fim - new Date(i.iniciadoEm).getTime()) / 1000));
            const txt = s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}min ${s % 60}s` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}min`;
            return i.encerradoEm ? txt : `${txt} ⏱`;
        },
        etapaAtual(i: ItemAoVivo): string {
            return i.etapas[0] ? rotuloEtapa(i.etapas[0].etapa) : 'abrindo';
        },
        etapasCronologicas(i: ItemAoVivo) {
            return [...i.etapas].reverse();
        },
        tomItem(i: ItemAoVivo): string {
            if (i.motivos?.includes('OPERADOR')) return 'operador';
            if (i.status === 'FALHOU' || i.status === 'ABANDONADA') return 'erro';
            if (i.status === 'EM_ANDAMENTO') return 'vivo';
            if (i.wame?.situacao === 'AGUARDANDO_ROTEAMENTO') return 'alerta';
            return 'ok';
        },
    },
});
</script>

<style scoped>
.oav { position: fixed; right: 16px; bottom: 16px; z-index: 1045; font-size: 12px; }
.oav-pill, .oav-head { touch-action: none; }
.oav-head { cursor: grab; }
.oav--arrastando { user-select: none; opacity: 0.92; }
.oav--arrastando .oav-head, .oav--arrastando .oav-pill { cursor: grabbing; }
.oav-pill {
    display: inline-flex; align-items: center; gap: 8px; border: 1px solid var(--bs-border-color, #e5e7eb);
    background: var(--bs-body-bg, #fff); color: var(--bs-body-color, #1f2937); border-radius: 999px; padding: 6px 12px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12); cursor: pointer;
}
.oav-pill__sep::before { content: '·'; margin-right: 6px; opacity: 0.5; }
.oav-pill--vivo { border-color: rgba(37, 99, 235, 0.5); }
.oav-pill--alerta { border-color: rgba(245, 158, 11, 0.6); }
.oav-pill--erro { border-color: rgba(220, 38, 38, 0.6); color: #b91c1c; }
.oav-pill--operador { border-color: rgba(124, 58, 237, 0.7); }
.oav-dot { width: 8px; height: 8px; border-radius: 50%; background: #94a3b8; flex-shrink: 0; }
.oav-pill--vivo .oav-dot, .oav-dot--pulsa { background: #2563eb; }
.oav-dot--pulsa { animation: oav-pulsa 1.4s infinite; }
@keyframes oav-pulsa { 0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.55); } 70% { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); } 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); } }
.oav-painel {
    width: min(460px, calc(100vw - 32px)); max-height: 72vh; display: flex; flex-direction: column;
    background: var(--bs-body-bg, #fff); color: var(--bs-body-color, #1f2937); border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 14px; box-shadow: 0 12px 40px rgba(15, 23, 42, 0.22); overflow: hidden;
}
.oav-head { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; border-bottom: 1px solid var(--bs-border-color, #e5e7eb); }
.oav-modo { font-size: 10px; padding: 1px 6px; border-radius: 999px; background: #e2e8f0; color: #334155; }
.oav-modo--sse { background: #dcfce7; color: #166534; }
.oav-modo--polling { background: #fef3c7; color: #92400e; }
.oav-vazio { padding: 18px; color: #64748b; display: flex; gap: 8px; align-items: flex-start; }
.oav-lista { list-style: none; margin: 0; padding: 6px; overflow-y: auto; }
.oav-item { border: 1px solid var(--bs-border-color, #e5e7eb); border-left-width: 4px; border-radius: 10px; padding: 8px 10px; margin-bottom: 6px; }
.oav-item--vivo { border-left-color: #2563eb; }
.oav-item--erro { border-left-color: #dc2626; }
.oav-item--alerta { border-left-color: #f59e0b; }
.oav-item--operador { border-left-color: #7c3aed; }
.oav-item--ok { border-left-color: #16a34a; }
.oav-item__topo { display: flex; align-items: center; gap: 6px; }
.oav-fluxo { font-weight: 600; }
.oav-tempo { margin-left: auto; color: #64748b; font-variant-numeric: tabular-nums; }
.oav-quem { margin-top: 2px; }
.oav-agora { display: flex; gap: 6px; align-items: center; margin-top: 4px; }
.oav-erro { margin-top: 2px; color: #b91c1c; word-break: break-word; }
.oav-operador { display: flex; gap: 6px; align-items: center; margin-top: 6px; padding: 6px 8px; border-radius: 8px; background: #f5f3ff; color: #5b21b6; }
.oav-wame { display: flex; gap: 6px; align-items: center; margin-top: 4px; flex-wrap: wrap; }
.oav-etapas { list-style: none; padding: 0; margin: 6px 0 0; display: flex; flex-wrap: wrap; gap: 4px; }
.oav-etapas li { display: inline-flex; align-items: center; gap: 3px; padding: 1px 6px; border-radius: 999px; background: #f1f5f9; font-size: 10.5px; }
.oav-etapa--SUCESSO { background: #dcfce7 !important; color: #166534; }
.oav-etapa--FALHA { background: #fee2e2 !important; color: #991b1b; }
.oav-etapa--INICIADA { background: #e0e7ff !important; color: #3730a3; }
.oav-acoes { display: flex; gap: 10px; margin-top: 6px; align-items: center; }
.oav-acoes :deep(.oav-encerrar) { --bs-btn-padding-y: 0.1rem; --bs-btn-padding-x: 0.4rem; --bs-btn-font-size: 0.72rem; color: #b91c1c; }
.oav-anomalias { margin-top: 4px; color: #b91c1c; font-size: 10.5px; word-break: break-word; }
.oav-rodape { padding: 4px 10px; color: #94a3b8; font-size: 10.5px; border-top: 1px solid var(--bs-border-color, #e5e7eb); }
</style>
